import { memo } from 'react';
import type { Book } from '@/types/catalog';

export type CoverTreatment = 'shadow' | 'flat' | 'frame';

interface BookCoverProps {
  book: Book;
  w: number;
  treatment?: CoverTreatment;
}

// Deterministic fallback palettes for real catalog rows that carry no palette.
const PALETTES: [string, string, string][] = [
  ['#1B2D4A', '#F1ECE0', '#B5121B'],
  ['#7A1F22', '#F1ECE0', '#0F2447'],
  ['#0F2447', '#E8D9B4', '#B5121B'],
  ['#2A4D3E', '#F1ECE0', '#D4A038'],
  ['#3F2A1E', '#E8D9B4', '#B5121B'],
  ['#4A2C5E', '#F1ECE0', '#D4A038'],
];
function paletteFor(id: string): [string, string, string] {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return PALETTES[h % PALETTES.length];
}

function AudioBadge({ w }: { w: number }) {
  return (
    <span
      style={{
        position: 'absolute',
        top: Math.max(6, w * 0.04),
        right: Math.max(6, w * 0.04),
        background: 'rgba(15,36,71,0.88)',
        color: '#FAFAF7',
        fontFamily: 'var(--sans)',
        fontSize: Math.max(9, w * 0.055),
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        padding: '3px 8px',
        borderRadius: 2,
      }}
    >
      Audio
    </span>
  );
}

function shadowFor(treatment: CoverTreatment): string {
  if (treatment === 'frame') return 'inset 0 0 0 1px rgba(15,36,71,0.18), 0 0 0 1px var(--rule-2)';
  if (treatment === 'flat') return '0 0 0 1px rgba(15,36,71,0.06)';
  return '0 1px 2px rgba(15,36,71,0.06), 0 10px 20px -10px rgba(15,36,71,0.22)';
}

function BookCoverInner({ book, w, treatment = 'shadow' }: BookCoverProps) {
  // Real cover image path.
  if (book.coverUrl) {
    return (
      <div
        style={{
          width: w,
          aspectRatio: '2/3',
          borderRadius: 2,
          overflow: 'hidden',
          position: 'relative',
          boxShadow: shadowFor(treatment),
          background: 'var(--rule-2, #E8D9B4)',
          transition: 'transform 220ms ease, box-shadow 220ms ease',
        }}
        aria-label={`Portada de ${book.title} por ${book.author}`}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={book.coverUrl}
          alt=""
          loading="lazy"
          style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
        />
        {book.product === 'audiobook' && <AudioBadge w={w} />}
      </div>
    );
  }

  const [bg, fg, accent] = book.palette ?? paletteFor(book.id);
  const variant = (book.id.charCodeAt(book.id.length - 1) + book.id.length) % 3;

  const wrapStyle: React.CSSProperties = {
    width: w,
    background: bg,
    color: fg,
    aspectRatio: '2/3',
    borderRadius: 2,
    position: 'relative',
    overflow: 'hidden',
    boxShadow: shadowFor(treatment),
    transition: 'transform 220ms ease, box-shadow 220ms ease',
  };

  const titleStyle: React.CSSProperties = {
    fontFamily: 'var(--serif)',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    lineHeight: 1.05,
    fontSize: Math.max(13, Math.min(28, w * 0.13)),
    textWrap: 'balance',
  };
  const authorStyle: React.CSSProperties = {
    fontFamily: 'var(--sans)',
    fontSize: Math.max(9, Math.min(13, w * 0.06)),
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    opacity: 0.86,
  };

  const footer = book.year ? `FCE · ${book.year}` : 'FCE';

  const stripe = (
    <div style={{ position: 'absolute', left: 0, right: 0, top: '22%', height: '4%', background: accent }} />
  );

  const imprint = (
    <div
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: w * 0.06,
        textAlign: 'center',
        fontFamily: 'var(--sans)',
        fontSize: Math.max(7, w * 0.045),
        letterSpacing: '0.22em',
        textTransform: 'uppercase',
        opacity: 0.7,
      }}
    >
      Biblioteca Digital
    </div>
  );

  if (variant === 0) {
    return (
      <div style={wrapStyle} aria-label={`Portada de ${book.title} por ${book.author}`}>
        <div style={{ position: 'absolute', inset: w * 0.08, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: w * 0.04 }}>
            <div style={{ ...authorStyle, opacity: 0.9 }}>{book.author}</div>
            <div style={{ width: w * 0.18, height: 1, background: fg, opacity: 0.6 }} />
          </div>
          <div style={titleStyle}>{book.title}</div>
          <div style={authorStyle}>{footer}</div>
        </div>
      </div>
    );
  }
  if (variant === 1) {
    return (
      <div style={wrapStyle} aria-label={`Portada de ${book.title} por ${book.author}`}>
        {stripe}
        <div style={{ position: 'absolute', inset: w * 0.08, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div style={{ ...authorStyle, marginTop: w * 0.02 }}>{book.author}</div>
          <div style={{ ...titleStyle, fontStyle: 'italic' }}>{book.title}</div>
          {imprint}
        </div>
      </div>
    );
  }
  return (
    <div style={wrapStyle} aria-label={`Portada de ${book.title} por ${book.author}`}>
      <div style={{ position: 'absolute', inset: w * 0.08, display: 'flex', flexDirection: 'column', gap: w * 0.06 }}>
        <div style={titleStyle}>{book.title}</div>
        <div style={{ width: w * 0.35, height: 1, background: fg, opacity: 0.6 }} />
        <div style={authorStyle}>{book.author}</div>
        <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'end' }}>
          <div style={{ ...authorStyle, fontSize: Math.max(8, w * 0.05) }}>{book.year ?? ''}</div>
          <div style={{ ...authorStyle, fontSize: Math.max(8, w * 0.05) }}>FCE</div>
        </div>
      </div>
    </div>
  );
}

export const BookCover = memo(BookCoverInner);
